import { useQuery } from "@tanstack/react-query";
import { ApiProject, ApiLocation } from "./api.types";
import { axiosClient } from "./axios.client";
import { Location } from "@/modules/shared/shared.types";
import { v4 as uuidv4 } from "uuid";

/**
 * React Query hook for fetching projects
 * @returns React Query result with projects data
 */
export function useApiProjects() {
  return useQuery({
    queryKey: ["projects"],
    queryFn: getProjects,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

type UseNearbyLocationsByProjectParams = {
  projectId: string;
  lat: number;
  long: number;
  enabled?: boolean;
};

/**
 * React Query hook for fetching nearby locations by project ID
 *
 * @param params - Project ID, location coordinates and optional enabled flag
 * @returns React Query result with nearby locations data
 */
export function useApiNearbyLocationsByProject(
  params: UseNearbyLocationsByProjectParams
) {
  const { projectId, lat, long, enabled = true } = params;

  return useQuery({
    queryKey: ["nearbyLocationsByProject", projectId, lat, long],
    queryFn: () => getNearbyLocationsByProject({ projectId, lat, long }),
    enabled: enabled && !!projectId && !!lat && !!long,
    staleTime: 1000 * 60 * 5, // 5 minutes - locations don't change frequently
  });
}

/**
 * Fetches the project list from the API
 * @returns Promise resolving to an array of ApiProject objects with "NM" stripped from names
 */
export const getProjects = async (): Promise<ApiProject[]> => {
  const res = await axiosClient.get<ApiProject[]>("/projects-list");
  return res.data.map((project) => ({
    ...project,
    name: project.name.replace(/NM/g, ""),
  }));
};

/**
 * Fetches nearby locations for a specific project from the API and maps to Location model
 * @param params - Project ID and location coordinates
 * @returns Promise resolving to an array of Location objects (mapped only, no filtering/ordering/slicing)
 */
export const getNearbyLocationsByProject = async (params: {
  projectId: string;
  lat: number;
  long: number;
}): Promise<Location[]> => {
  const { projectId, lat, long } = params;
  const res = await axiosClient.get<ApiLocation[]>(
    `/${projectId}/get-nearby-locations`,
    {
      params: { lat, long },
    }
  );

  // Map API response to Location model
  const locationsModels = res.data
    .filter((el) => !!el.coords.latitude && !!el.coords.longitude)
    .map<Location>((el) => ({
      id: uuidv4(),
      description: el.name ?? "",
      imagesUrls: el.imgs?.map((img) => img.url) ?? [],
      coords: {
        lat: Number(el.coords.latitude),
        longt: Number(el.coords.longitude),
        hash: "",
      },
      distance: 10 * Math.floor((el.distanceFromPoint ?? 0) / 10),
    }));

  return locationsModels;
};
